import isUndefined from "lodash/isUndefined";

import type { Node, NodeId } from "~/models/dto";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { Logger } from "~/tools/logger";
import type { Record } from "~/tools/record";
import type { SparseArray } from "~/tools/sparse-array";

const logger = Logger.namespace("GlobalStoreHelper");

export type NodeTypes = Partial<Record<string, SparseArray<NodeId, any>>>;

export const optionalFieldFor = <T extends Node>(node: T, nodeTypes: NodeTypes): undefined | SparseArray<NodeId, T> =>
	nodeTypes[node._type];

export function mergeGlobalStoreNodes<T extends Node>(newNodes: Array<T>, nodeTypes: NodeTypes): void {
	if (newNodes.length === 0) {
		return;
	}

	const field = optionalFieldFor(newNodes[0], nodeTypes);

	if (field) {
		const nodesToMerge: Array<T> = newNodes.filter((newNode) => {
			if (!newNode.id) {
				return false;
			}

			const currentNode = field.get(newNode.id);
			return isFieldMergeNecessary(currentNode, newNode);
		});

		if (nodesToMerge.length === 0) {
			return;
		}

		field.mergeArray(nodesToMerge, ({ id }: T) => id!);
	} else {
		logger.warn(`Got unexpected type '${newNodes[0]._type}' while merging global node.`);
	}
}

export const isFieldMergeNecessary = <TNode extends Node>(currentNode: TNode | undefined, newNode: TNode): boolean => {
	// In case there is no node some merging should happen
	if (isUndefined(currentNode)) {
		return true;
	}
	// No merging is necessary if the current node is fresher than the old one
	// And no merging is necessary if those two are the same
	return (
		(currentNode.lastUpdateTimestamp || 0) <= (newNode.lastUpdateTimestamp || 0) && !isDeepEqual(currentNode, newNode)
	);
};
